// Health — live status of every external service, so an admin can tell at a glance what broke
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@startus/lib';
import { fetchSystemStatus } from '../../services/system-health.service';
import type {
  AiModels,
  ServiceCheck,
  ServiceStatus,
  SystemStatus,
} from '../../types/system-health';
import { sortByAttention, summarize } from '../../utils/system-health';
import type { HealthSummary } from '../../utils/system-health';

const SUMMARY_ALERT: Record<HealthSummary['level'], string> = {
  down: 'alert-error',
  unknown: 'alert-warning',
  ok: 'alert-success',
};

const STATUS_BADGE: Record<ServiceStatus, string> = {
  up: 'badge-success',
  down: 'badge-error',
  unknown: 'badge-warning',
  not_configured: 'badge-ghost',
};

function ServiceRow({ service }: { service: ServiceCheck }) {
  const { t } = useTranslation('admin');

  return (
    <li className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <p className="font-semibold">
          {t(`health.services.${service.id}.name`, { defaultValue: service.id })}
        </p>
        <p className="text-sm text-white/60">
          {t(`health.services.${service.id}.impact`, { defaultValue: '' })}
        </p>
        {service.message && service.status !== 'up' && (
          <p className="mt-1 text-sm break-words text-white/80">{service.message}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {service.latencyMs !== null && service.status === 'up' && (
          <span className="text-sm text-white/50">
            {t('health.latency', { ms: service.latencyMs })}
          </span>
        )}
        <span className={`badge ${STATUS_BADGE[service.status]}`}>
          {t(`health.status.${service.status}`)}
        </span>
      </div>
    </li>
  );
}

function AiModelsCard({ ai }: { ai: AiModels }) {
  const { t } = useTranslation('admin');
  const rows: { key: string; value: string | null }[] = [
    { key: 'chatModel', value: ai.chatModel },
    { key: 'profileModel', value: ai.profileModel },
    { key: 'transcriptionModel', value: ai.transcriptionModel },
  ];

  return (
    <section className="bg-[#1E1E1E] rounded-2xl p-6">
      <h2 className="text-lg font-semibold">{t('health.ai.title')}</h2>
      {ai.chatModel === null ? (
        <p className="mt-2 text-sm text-white/60">{t('health.ai.unavailable')}</p>
      ) : (
        <dl className="mt-3 space-y-2">
          {rows.map((row) => (
            <div key={row.key} className="flex flex-wrap justify-between gap-x-4 text-sm">
              <dt className="text-white/60">{t(`health.ai.${row.key}`)}</dt>
              <dd className="font-mono">{row.value ?? '-'}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

const HealthPage: React.FC = () => {
  const { t, i18n } = useTranslation(['common', 'admin']);
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Only sets state from promise callbacks, so it can run from the mount effect
  const fetchStatus = useCallback(() => {
    fetchSystemStatus()
      .then(setStatus)
      .catch((err: unknown) => {
        captureException(err, { page: 'admin-health' });
        console.error('[HealthPage] Failed to fetch system status:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const checkAgain = () => {
    setLoading(true);
    setError(false);
    fetchStatus();
  };

  const services = useMemo(() => (status ? sortByAttention(status.services) : []), [status]);
  const summary = useMemo(() => summarize(services), [services]);

  const checkedAt = status
    ? new Date(status.checkedAt).toLocaleTimeString(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('admin:health.title')}</h1>
          <p className="text-sm text-white/60">
            {checkedAt
              ? t('admin:health.checkedAt', { time: checkedAt })
              : t('admin:health.subtitle')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary w-full sm:w-auto"
          onClick={checkAgain}
          disabled={loading}
        >
          {loading && <span className="loading loading-spinner loading-sm" />}
          {loading ? t('admin:health.checking') : t('admin:health.checkAgain')}
        </button>
      </div>

      {error && (
        <div role="alert" className="alert alert-error">
          <span>{t('admin:health.loadError')}</span>
        </div>
      )}

      {loading && !status && (
        <div className="flex flex-col items-center gap-3 py-16 text-white/60">
          <span className="loading loading-spinner loading-lg" />
          <p className="text-sm">{t('admin:health.checkingHint')}</p>
        </div>
      )}

      {status && (
        <>
          <div role="status" className={`alert ${SUMMARY_ALERT[summary.level]}`}>
            <span className="font-semibold">
              {t(`admin:health.summary.${summary.level}`, { count: summary.count })}
            </span>
          </div>

          <section className="bg-[#1E1E1E] rounded-2xl px-6">
            <ul className="divide-y divide-white/10">
              {services.map((service) => (
                <ServiceRow key={service.id} service={service} />
              ))}
            </ul>
          </section>

          <AiModelsCard ai={status.ai} />
        </>
      )}
    </div>
  );
};

export default HealthPage;
